import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import useAuthStore from '../../store/authStore';

export default function MobileHeader({ onMenuOpen, title, showBack, onBack }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuthStore();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/documents?search=${encodeURIComponent(searchQuery.trim())}`;
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-theme-secondary border-b border-theme-primary safe-area-top">
        <div className="flex items-center justify-between h-14 px-4">
          {/* Left: Menu or Back */}
          {showBack ? (
            <button
              onClick={onBack}
              className="p-2 -ml-2 rounded-full hover:bg-theme-tertiary active:bg-theme-tertiary transition-colors"
            >
              <svg className="w-6 h-6 text-theme-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={onMenuOpen}
              className="p-2 -ml-2 rounded-full hover:bg-theme-tertiary active:bg-theme-tertiary transition-colors"
            >
              <Bars3Icon className="w-6 h-6 text-theme-primary" />
            </button>
          )}

          {/* Center: Title or Logo */}
          {title ? (
            <h1 className="text-lg font-semibold text-theme-primary truncate max-w-[200px]">
              {title}
            </h1>
          ) : (
            <Link to="/dashboard" className="flex items-center gap-2">
              <img
                src="/images/lensverse_logo.png"
                alt="LensVerse"
                className="h-8 w-8"
              />
              <span className="text-lg font-bold text-theme-primary">LensVerse</span>
            </Link>
          )}

          {/* Right: Search & Profile */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-full hover:bg-theme-tertiary active:bg-theme-tertiary transition-colors"
            >
              <MagnifyingGlassIcon className="w-6 h-6 text-theme-muted" />
            </button>
            <div className="p-1">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center text-white font-semibold text-sm">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search Overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-theme-primary"
          >
            <div className="safe-area-top">
              <form onSubmit={handleSearch} className="flex items-center h-14 px-4 gap-3">
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="p-2 -ml-2 rounded-full hover:bg-theme-tertiary transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-theme-primary" />
                </button>
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="flex-1 bg-transparent text-theme-primary placeholder-theme-muted text-lg outline-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="p-2 rounded-full hover:bg-theme-tertiary transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-theme-muted" />
                  </button>
                )}
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
